import { domainTrace } from '../../diagnostics/domainTrace';
import { StaleReferenceError } from '../../errors/DomainError';
import { Patient } from '../../patient/patient';
import { StructureSet } from '../../patient/structureset';

export interface StructureSetExportOptions {
    /**
     * When provided, only these ROI numbers are included in the export
     * (delegates to {@link StructureSet.exportWith}). When omitted, the
     * whole structure set is exported.
     */
    selectedRoiNumbers?: number[];
    /** Optional DICOM character set override (e.g. 'ISO_IR 100'). */
    characterSet?: string;
    /**
     * When true, export as a Monaco DIR file rather than DICOM RTSTRUCT.
     * Requires the SS to be bound to a CT/MR image.
     */
    asMonacoFile?: boolean;
    /** Patient name embedded in the Monaco file. Used only when asMonacoFile. */
    patientName?: string;
}

/**
 * Read-only domain service that produces export blobs from a structure
 * set without exposing the live {@link StructureSet} entity to UI code.
 *
 * The service does NOT call `environment.saveChanges()` — syncing
 * engine contour state into the domain is an engine/UI concern. Callers
 * must save engine changes before invoking the service.
 */
export class StructureSetExportService {
    constructor(private readonly patient: Patient) {}

    /**
     * Produce an export blob for the structure set with the given SOP
     * Instance UID.
     *
     * @throws {StaleReferenceError} if no SS with that UID exists.
     */
    exportBlob(
        structureSetUid: string,
        options: StructureSetExportOptions = {}
    ): Blob {
        domainTrace.service('StructureSetExportService.exportBlob', {
            structureSetUid,
            roiSubsetCount: options.selectedRoiNumbers?.length,
            asMonacoFile: options.asMonacoFile ?? false,
        });
        const ss = this.resolve(structureSetUid);
        if (options.asMonacoFile) {
            return ss.exportAsMonacoFile(options.patientName ?? 'NO_NAME');
        }
        if (options.selectedRoiNumbers) {
            return ss.exportWith(
                options.selectedRoiNumbers,
                options.characterSet
            );
        }
        return ss.export();
    }

    private resolve(structureSetUid: string): StructureSet {
        const ss =
            this.patient.getStructureSetWithSopInstanceUid(structureSetUid);
        if (!ss) {
            throw new StaleReferenceError(
                `StructureSet ${structureSetUid} no longer exists.`
            );
        }
        return ss;
    }
}
